/**
 * Modal de confirmação pra sincronizar categorias da esteira.
 *
 * Detecta as categorias das pages atualmente vinculadas ao grupo e
 * re-sincroniza — puxando pages NOVAS que foram adicionadas a essas
 * categorias depois do grupo ter sido criado.
 *
 * Idempotente — pages já vinculadas viram skip. Espelho do
 * SyncFlowCategoriesDialog.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, RefreshCw, Tag, CheckCircle2 } from 'lucide-react';
import { syncEsteiraGroupFromCategories } from '@/services/api';
import { useToast } from '@/hooks/use-toast';

interface Category {
    id: number;
    name: string;
}

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    groupId: number;
    groupName: string;
    categories: Category[];
}

export default function SyncEsteiraCategoriesDialog({
    open, onOpenChange, groupId, groupName, categories,
}: Props) {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [result, setResult] = useState<{
        added: number;
        skipped: number;
        skipped_no_permission: number;
        total: number;
    } | null>(null);

    const mutation = useMutation({
        mutationFn: () => syncEsteiraGroupFromCategories(
            groupId, categories.map(c => c.id),
        ),
        onSuccess: (res) => {
            setResult({
                added: res.added,
                skipped: res.skipped_existing,
                skipped_no_permission: res.skipped_no_permission,
                total: res.total_eligible,
            });
            queryClient.invalidateQueries({ queryKey: ['esteira-groups'] });
            queryClient.invalidateQueries({ queryKey: ['esteira-group', groupId] });

            if (res.added > 0) {
                toast({ title: `${res.added} página${res.added > 1 ? 's' : ''} adicionada${res.added > 1 ? 's' : ''}` });
            } else if (res.total_eligible === 0) {
                toast({
                    variant: 'destructive',
                    title: 'Nenhuma página ativa nessas categorias',
                });
            } else {
                toast({ title: 'Tudo sincronizado — nada a fazer' });
            }
        },
        onError: (e: any) => {
            toast({
                variant: 'destructive',
                title: 'Falha no sync',
                description: e?.response?.data?.detail || 'Erro ao sincronizar.',
            });
        },
    });

    const handleClose = (next: boolean) => {
        if (!next) {
            setResult(null);
            mutation.reset();
        }
        onOpenChange(next);
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <RefreshCw className="h-5 w-5 text-indigo-600" />
                        Sincronizar categorias
                    </DialogTitle>
                    <DialogDescription>
                        Esteira <strong>{groupName}</strong> — adiciona todas as páginas
                        ativas das categorias abaixo. Páginas já vinculadas não
                        duplicam (idempotente). Replenish dispara após o sync.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-600 uppercase tracking-wider">
                        {categories.length} categoria{categories.length > 1 ? 's' : ''} vinculada{categories.length > 1 ? 's' : ''}
                    </p>
                    <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
                        {categories.map(c => (
                            <span
                                key={c.id}
                                className="inline-flex items-center gap-1 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200/60 rounded-md px-2 py-1"
                            >
                                <Tag className="w-3 h-3" />
                                {c.name}
                            </span>
                        ))}
                    </div>
                </div>

                {result && (
                    <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm">
                        <div className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                            <div className="space-y-1">
                                <p className="font-medium text-emerald-900">Sincronização concluída</p>
                                <ul className="text-xs text-emerald-800 space-y-0.5">
                                    <li>• Adicionadas: <strong>{result.added}</strong></li>
                                    <li>• Já vinculadas (skip): <strong>{result.skipped}</strong></li>
                                    {result.skipped_no_permission > 0 && (
                                        <li>• Sem permissão (skip): <strong>{result.skipped_no_permission}</strong></li>
                                    )}
                                    <li>• Total elegível nas categorias: <strong>{result.total}</strong></li>
                                </ul>
                            </div>
                        </div>
                    </div>
                )}

                <DialogFooter className="gap-2">
                    <Button
                        variant="outline"
                        onClick={() => handleClose(false)}
                        disabled={mutation.isPending}
                    >
                        {result ? 'Fechar' : 'Cancelar'}
                    </Button>
                    {!result && (
                        <Button
                            onClick={() => mutation.mutate()}
                            disabled={mutation.isPending || categories.length === 0}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white"
                        >
                            {mutation.isPending
                                ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Sincronizando…</>
                                : <><RefreshCw className="w-4 h-4 mr-1.5" /> Sincronizar</>}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
